import ArgType from 'notebook/tables/excess/ArgType';
import FunctionCategory from 'notebook/tables/excess/FunctionCategory';
import {Range} from 'notebook/tables/excess/FunctionConstants';
import {TypedValueSpreadsheetFunction} from 'notebook/tables/excess/FunctionConstants';
import * as util from 'notebook/tables/excess/Util';
import {lookupTypedValue} from 'notebook/tables/excess/util/lookupTypedValue';
import {TypedValue} from 'notebook/tables/TableConstants';
import {coerceToPrimitive} from 'notebook/tables/typed-values/DataTypes';

export default {
  name: 'XLOOKUP',
  relatedNames: [
    // XLOOKUP is a better alternative to the following 3 functions:
    'LOOKUP',
    'VLOOKUP',
    'HLOOKUP',
  ],
  category: FunctionCategory.LOOKUP,
  shortDescription: 'Searches a value to return an item from a range.',
  description:
    'Returns the values in the result range based on the position where a match was found in the search range. Errors if no exact match.',
  params: [
    {
      name: 'search_key',
      type: ArgType.singularValue,
      description: 'The value to search for. For example, 5, "LOT123", or A2.',
    },
    {
      name: 'search_range',
      type: ArgType.cellRange,
      description: 'The range to consider for the search. This range must be a singular row or column.',
      // since we are searching over these values, not coercing them, we can have errors in the range.
      allowError: true,
    },
    {
      name: 'result_range',
      type: ArgType.cellRange,
      optional: true,
      description:
        "The range to consider for the result. This range's row or column size should be the same as search_range.",
      // since we are searching over these values, not coercing them, we can have errors in the range.
      allowError: true,
    },
    {
      name: 'if_not_found',
      type: ArgType.singularValue,
      optional: true,
      description: 'The value to return if no match is found.',
    },
  ],

  evalWithTypedValues: (
    searchKey: TypedValue,
    searchRange: Range<TypedValue>,
    resultRange: Range<TypedValue>,
    ifNotFound: TypedValue | null
  ): TypedValue => {
    const {result} = lookupTypedValue(searchKey, searchRange, resultRange, 'XLOOKUP');
    if (!result) {
      if (ifNotFound != null) {
        return ifNotFound;
      }
      throw new util.NotApplicableError(
        `Did not find value "${coerceToPrimitive(searchKey)}" in XLOOKUP evaluation.`
      );
    }
    return result;
  },
} satisfies TypedValueSpreadsheetFunction;
